/**
 * InvoiceForm.jsx
 *
 * Create / Edit invoice with:
 *   - Customer autocomplete
 *   - Product autocomplete with live GST fill
 *   - Live CGST/SGST/IGST calculation per line
 *   - Invoice-level discount
 *   - Live grand total panel
 *   - Template selector for PDF
 *
 * PASTE AT: frontend/src/pages/admin/accounting/InvoiceForm.jsx  (NEW FILE)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { invoiceAPI, contactsAPI, inventoryAPI } from '../../../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, TrashIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

// ── GST rates ─────────────────────────────────────────────────
const GST_RATES = [0, 5, 12, 18, 28];

// ── Blank item ────────────────────────────────────────────────
const blankItem = () => ({
  _key:          Math.random(),
  productId:     null,
  name:          '',
  description:   '',
  hsnCode:       '',
  unit:          'PCS',
  quantity:      1,
  rate:          0,
  discountType:  'PERCENTAGE',
  discountValue: 0,
  gstRate:       18,
  taxType:       'GST',
  taxInclusive:  false,
  cessRate:      0,
  // computed
  subtotal:      0,
  discountAmount:0,
  taxableAmount: 0,
  cgstRate:      9,
  sgstRate:      9,
  igstRate:      0,
  cgstAmount:    0,
  sgstAmount:    0,
  igstAmount:    0,
  lineTotal:     0,
});

// ── Pure math helpers ─────────────────────────────────────────
function round2(v) { return Math.round((Number(v) + Number.EPSILON) * 100) / 100; }

function calcItem(item, isInterState) {
  const qty   = Number(item.quantity) || 0;
  const rate  = Number(item.rate) || 0;
  const dv    = Number(item.discountValue) || 0;
  const gst   = Number(item.gstRate) || 0;
  const cess  = Number(item.cessRate) || 0;

  const subtotal      = round2(qty * rate);
  const discountAmount = item.discountType === 'AMOUNT'
    ? round2(Math.min(dv, subtotal))
    : round2((subtotal * dv) / 100);
  const taxableAmount  = round2(subtotal - discountAmount);

  let cgstRate = 0, sgstRate = 0, igstRate = 0;
  let cgstAmount = 0, sgstAmount = 0, igstAmount = 0;

  if (item.taxType !== 'EXEMPT' && gst > 0) {
    if (isInterState) {
      igstRate   = gst;
      igstAmount = round2((taxableAmount * gst) / 100);
    } else {
      cgstRate   = gst / 2;
      sgstRate   = gst / 2;
      cgstAmount = round2((taxableAmount * cgstRate) / 100);
      sgstAmount = round2((taxableAmount * sgstRate) / 100);
    }
  }

  const cessAmount = round2((taxableAmount * cess) / 100);
  const totalTax   = round2(cgstAmount + sgstAmount + igstAmount + cessAmount);
  const lineTotal  = round2(taxableAmount + totalTax);

  return { ...item, subtotal, discountAmount, taxableAmount, cgstRate, sgstRate, igstRate, cgstAmount, sgstAmount, igstAmount, cessAmount, totalTax, lineTotal };
}

function calcTotals(items, isInterState) {
  const calced = items.map(i => calcItem(i, isInterState));
  const subtotal      = round2(calced.reduce((s, i) => s + i.subtotal, 0));
  const discountAmount = round2(calced.reduce((s, i) => s + i.discountAmount, 0));
  const taxableAmount  = round2(calced.reduce((s, i) => s + i.taxableAmount, 0));
  const cgstAmount     = round2(calced.reduce((s, i) => s + i.cgstAmount, 0));
  const sgstAmount     = round2(calced.reduce((s, i) => s + i.sgstAmount, 0));
  const igstAmount     = round2(calced.reduce((s, i) => s + i.igstAmount, 0));
  const cessAmount     = round2(calced.reduce((s, i) => s + i.cessAmount, 0));
  const totalTax       = round2(cgstAmount + sgstAmount + igstAmount + cessAmount);
  const rawTotal       = round2(taxableAmount + totalTax);
  const grandTotal     = Math.round(rawTotal);
  const roundOff       = round2(grandTotal - rawTotal);
  return { calced, subtotal, discountAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount, totalTax, roundOff, grandTotal };
}

// ── Customer Autocomplete ─────────────────────────────────────
// Uses your existing /api/crm endpoint directly
function CustomerSearch({ value, onSelect }) {
  const [q, setQ]             = useState(value?.name || '');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const timer                 = useRef(null);

  useEffect(() => { if (value?.name) setQ(value.name); }, [value]);

  const search = v => {
    setQ(v);
    clearTimeout(timer.current);
    if (!v || v.length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        // Call your existing CRM endpoint — type=customer, search by name/company/email
        const res = await contactsAPI.getAll({ search: v, type: 'customer', limit: 10 });
        // contactsAPI returns { data: [...] } via sendPaginated
        const list = res.data || res.contacts || [];
        setResults(list);
        setOpen(list.length > 0);
      } catch { setResults([]); }
    }, 300);
  };

  const pick = c => {
    setQ(c.company || c.name);
    setOpen(false);
    setResults([]);
    // Map CRM fields to invoice customer shape
    onSelect({
      _id:            c._id,
      name:           c.company || c.name,
      displayName:    c.name,
      gstin:          c.gstin || '',
      pan:            c.pan || '',
      phone:          c.phone || '',
      email:          c.email || '',
      stateCode:      c.gstin ? c.gstin.substring(0, 2) : '',
      creditLimit:    c.creditLimit || 0,
      outstandingBalance: c.outstandingBalance || 0,
      // Billing address from CRM address object
      billingAddress: c.address?.street || '',
      billingCity:    c.address?.city || '',
      billingState:   c.address?.state || '',
      billingPincode: c.address?.postalCode || '',
    });
  };

  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={q}
          onChange={e => search(e.target.value)}
          onFocus={() => q.length >= 2 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Type customer name, phone or GSTIN..."
          className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg max-h-56 overflow-y-auto">
          {results.map(c => (
            <button key={c._id} type="button" onMouseDown={() => pick(c)}
              className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 border-b border-gray-50 last:border-0">
              <p className="text-sm font-semibold text-gray-900">{c.company || c.name}</p>
              <p className="text-xs text-gray-400">
                {[c.name !== (c.company || c.name) ? c.name : null, c.phone, c.gstin].filter(Boolean).join(' · ')}
              </p>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && q.length >= 2 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg px-4 py-3 text-sm text-gray-400">
          No customers found for "{q}"
        </div>
      )}
    </div>
  );
}

// ── Product Autocomplete ──────────────────────────────────────
// Uses your existing /api/inventory/search endpoint
function ProductSearch({ onSelect }) {
  const [q, setQ]         = useState('');
  const [results, setRes] = useState([]);
  const [open, setOpen]   = useState(false);
  const timer             = useRef(null);

  const search = v => {
    setQ(v);
    clearTimeout(timer.current);
    if (!v || v.length < 2) { setRes([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        // Use your existing inventory search endpoint
        const res = await inventoryAPI.search(v, 10);
        const list = res.data || [];
        setRes(list);
        setOpen(list.length > 0);
      } catch { setRes([]); }
    }, 300);
  };

  const pick = p => {
    setQ('');
    setOpen(false);
    setRes([]);
    onSelect(p);
  };

  return (
    <div className="relative flex-1">
      <input value={q} onChange={e => search(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search product / service by name, SKU, HSN..."
        className="w-full px-3 py-1.5 border border-dashed border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50" />
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 mt-1 w-96 bg-white rounded-xl border border-gray-200 shadow-lg max-h-56 overflow-y-auto">
          {results.map(p => (
            <button key={p._id} type="button" onMouseDown={() => pick(p)}
              className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 border-b border-gray-50 last:border-0">
              <p className="text-sm font-semibold text-gray-900">{p.name}</p>
              <p className="text-xs text-gray-400">
                {[
                  p.sku     ? `SKU: ${p.sku}` : null,
                  p.hsnCode ? `HSN: ${p.hsnCode}` : null,
                  p.sellingPrice ? `₹${p.sellingPrice}` : null,
                  p.gstRate != null ? `GST ${p.gstRate}%` : null,
                  p.quantity != null ? `Stock: ${p.quantity} ${p.unit}` : null,
                ].filter(Boolean).join(' · ')}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Form ─────────────────────────────────────────────────
export function InvoiceForm({ editing, docType = 'INVOICE', onSave, onClose }) {
  const [saving, setSaving] = useState(false);
  const [customer, setCust] = useState(null);
  const [isInterState, setInterState] = useState(false);
  const [items, setItems]   = useState([blankItem()]);
  const [form, setForm]     = useState({
    invoiceDate:   new Date().toISOString().split('T')[0],
    dueDate:       '',
    placeOfSupply: '',
    poNumber:      '',
    notes:         '',
    termsConditions: '',
    discountType:  'PERCENTAGE',
    discountValue: 0,
    taxInclusive:  false,
    asDraft:       false,
    companyStateCode: '24', // Gujarat default — change to your state
  });

  // Pre-fill when editing
  useEffect(() => {
    if (!editing) return;
    setCust(editing.customerId);
    setForm({
      invoiceDate:      editing.invoiceDate?.split('T')[0] || '',
      dueDate:          editing.dueDate?.split('T')[0] || '',
      placeOfSupply:    editing.placeOfSupply || '',
      poNumber:         editing.poNumber || '',
      notes:            editing.notes || '',
      termsConditions:  editing.termsConditions || '',
      discountType:     editing.discountType || 'PERCENTAGE',
      discountValue:    editing.discountValue || 0,
      taxInclusive:     editing.taxInclusive || false,
      asDraft:          editing.status === 'DRAFT',
      companyStateCode: '24',
    });
    setInterState(editing.isIgst || false);
    if (editing.items?.length) {
      setItems(editing.items.map(i => ({
        ...blankItem(),
        ...i,
        gstRate: i.taxRate || 18,
        _key: Math.random(),
      })));
    }
  }, [editing]);

  // Detect inter-state from customer GSTIN state code
  const onSelectCustomer = c => {
    setCust(c);
    // stateCode comes from first 2 digits of GSTIN (set in CustomerSearch.pick)
    const custState = c.stateCode || (c.gstin ? c.gstin.substring(0, 2) : '');
    const inter     = !!(custState && form.companyStateCode && custState !== form.companyStateCode);
    setInterState(inter);
    if (custState) setForm(f => ({ ...f, placeOfSupply: custState }));
  };

  // When product is selected from inventory autocomplete
  const onSelectProduct = p => {
    const newItem = {
      ...blankItem(),
      productId:    p._id,
      name:         p.name,
      description:  p.description || '',
      hsnCode:      p.hsnCode || p.sacCode || '',
      unit:         p.unit || 'PCS',
      rate:         Number(p.sellingPrice || 0),
      gstRate:      Number(p.gstRate ?? 18),
      cessRate:     Number(p.cessRate || 0),
      taxType:      p.taxPreference === 'EXEMPT' ? 'EXEMPT' : 'GST',
      taxInclusive: Boolean(p.taxInclusive),
    };
    setItems(prev => [...prev, newItem]);
  };

  // Update item field
  const updateItem = (key, field, value) => {
    setItems(prev => prev.map(i => i._key === key ? { ...i, [field]: value } : i));
  };

  const removeItem = key => setItems(prev => prev.filter(i => i._key !== key));
  const addItem    = ()  => setItems(prev => [...prev, blankItem()]);

  // Totals
  const totals = calcTotals(items, isInterState);

  // ── Submit ────────────────────────────────────────────────
  const submit = async (asDraftOverride = false) => {
    if (!customer) { toast.error('Select a customer'); return; }
    if (!items.length) { toast.error('Add at least one item'); return; }
    if (items.some(i => !i.name || !i.rate)) { toast.error('Fill all item names and rates'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        asDraft:    asDraftOverride,
        type:       docType,
        customerId: customer._id,
        customer:   {
          name:            customer.name,
          gstin:           customer.gstin || '',
          billingAddress:  customer.billingAddress || '',
          billingCity:     customer.billingCity || '',
          billingState:    customer.billingState || '',
          billingPincode:  customer.billingPincode || '',
        },
        isInterState,
        items: items.map(i => ({
          productId:    i.productId || null,
          name:         i.name,
          description:  i.description,
          hsnCode:      i.hsnCode,
          unit:         i.unit,
          quantity:     Number(i.quantity),
          rate:         Number(i.rate),
          discountType: i.discountType,
          discountValue:Number(i.discountValue),
          gstRate:      Number(i.gstRate),
          cessRate:     Number(i.cessRate),
          taxType:      i.taxType,
          taxInclusive: i.taxInclusive,
        })),
      };

      editing
        ? await invoiceAPI.update(editing._id, payload)
        : await invoiceAPI.create(payload);

      toast.success(editing ? 'Updated successfully' : 'Created successfully');
      onSave();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inp    = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const label  = 'text-xs font-semibold text-gray-500 uppercase mb-1 block';

  return (
    <form onSubmit={e => e.preventDefault()} className="space-y-6">

      {/* ── Customer ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className={label}>Customer *</label>
          <CustomerSearch value={customer} onSelect={onSelectCustomer} />
          {customer && (
            <div className="mt-2 bg-indigo-50 rounded-xl px-3 py-2 text-xs text-gray-600">
              <span className="font-semibold">{customer.name}</span>
              {customer.gstin && <span className="ml-2 font-mono text-indigo-600">{customer.gstin}</span>}
              {isInterState && <span className="ml-2 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">IGST (Inter-state)</span>}
              {!isInterState && customer._id && <span className="ml-2 bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">CGST + SGST</span>}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Invoice Date</label>
            <input type="date" value={form.invoiceDate} onChange={e => setForm(f => ({ ...f, invoiceDate: e.target.value }))} className={inp} />
          </div>
          <div>
            <label className={label}>Due Date</label>
            <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} className={inp} />
          </div>
          <div>
            <label className={label}>Place of Supply</label>
            <input value={form.placeOfSupply} onChange={e => setForm(f => ({ ...f, placeOfSupply: e.target.value }))} placeholder="24" maxLength={2} className={inp} />
          </div>
          <div>
            <label className={label}>WO / PO No.</label>
            <input value={form.poNumber} onChange={e => setForm(f => ({ ...f, poNumber: e.target.value }))} placeholder="PU/WO/26/08" className={inp} />
          </div>
        </div>
      </div>

      {/* ── Items ────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className={label}>Items</label>
          <div className="flex gap-2">
            <ProductSearch onSelect={onSelectProduct} />
            <button type="button" onClick={addItem}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-100">
              <PlusIcon className="w-4 h-4" /> Add Row
            </button>
          </div>
        </div>

        {/* Items table */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#','Description','HSN','Qty','Unit','Rate (₹)','Disc%','GST%','Taxable','Tax','Total',''].map(h => (
                    <th key={h} className="text-xs font-semibold text-gray-400 px-2 py-2.5 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item, idx) => {
                  const c = calcItem(item, isInterState);
                  return (
                    <tr key={item._key} className="hover:bg-gray-50">
                      <td className="px-2 py-2 text-gray-400 text-xs w-8">{idx + 1}</td>
                      <td className="px-2 py-2 min-w-[140px]">
                        <input value={item.name} onChange={e => updateItem(item._key, 'name', e.target.value)}
                          placeholder="Item name" className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" required />
                        <input value={item.hsnCode} onChange={e => updateItem(item._key, 'hsnCode', e.target.value)}
                          placeholder="HSN/SAC" className="w-full mt-1 px-2 py-0.5 border border-gray-100 rounded-lg text-xs text-gray-400 focus:outline-none" />
                      </td>
                      <td className="px-2 py-2 w-16">
                        <span className="text-xs text-gray-400">{item.hsnCode || '—'}</span>
                      </td>
                      <td className="px-2 py-2 w-16">
                        <input type="number" min="0.001" step="0.001" value={item.quantity}
                          onChange={e => updateItem(item._key, 'quantity', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                      </td>
                      <td className="px-2 py-2 w-16">
                        <select value={item.unit} onChange={e => updateItem(item._key, 'unit', e.target.value)}
                          className="w-full px-1 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none">
                          {['PCS','KG','LITRE','METER','BOX','SET','SQF','SQM','HOUR'].map(u => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-2 w-24">
                        <input type="number" min="0" step="0.01" value={item.rate}
                          onChange={e => updateItem(item._key, 'rate', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                      </td>
                      <td className="px-2 py-2 w-16">
                        <input type="number" min="0" max="100" step="0.1" value={item.discountValue}
                          onChange={e => updateItem(item._key, 'discountValue', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                      </td>
                      <td className="px-2 py-2 w-16">
                        <select value={item.gstRate} onChange={e => updateItem(item._key, 'gstRate', Number(e.target.value))}
                          className="w-full px-1 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none">
                          {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2 w-24 text-right text-xs text-gray-600">₹{c.taxableAmount.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 w-24 text-right text-xs">
                        {isInterState
                          ? <span className="text-orange-600">IGST ₹{c.igstAmount.toLocaleString('en-IN')}</span>
                          : <span className="text-blue-600">₹{(c.cgstAmount + c.sgstAmount).toLocaleString('en-IN')}</span>}
                      </td>
                      <td className="px-2 py-2 w-24 text-right font-semibold text-gray-900">₹{c.lineTotal.toLocaleString('en-IN')}</td>
                      <td className="px-2 py-2 w-8">
                        <button type="button" onClick={() => removeItem(item._key)} className="p-1 hover:bg-red-50 rounded-lg">
                          <TrashIcon className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Totals panel ─────────────────────────────────── */}
      <div className="flex justify-end">
        <div className="w-80 bg-gray-50 rounded-2xl p-4 space-y-2 text-sm border border-gray-200">
          <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>₹{totals.subtotal.toLocaleString('en-IN')}</span></div>
          {totals.discountAmount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>−₹{totals.discountAmount.toLocaleString('en-IN')}</span></div>}
          <div className="flex justify-between text-gray-500"><span>Taxable Amount</span><span>₹{totals.taxableAmount.toLocaleString('en-IN')}</span></div>
          {isInterState
            ? <div className="flex justify-between text-orange-600"><span>IGST</span><span>₹{totals.igstAmount.toLocaleString('en-IN')}</span></div>
            : <>
                <div className="flex justify-between text-blue-600"><span>CGST</span><span>₹{totals.cgstAmount.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-blue-600"><span>SGST</span><span>₹{totals.sgstAmount.toLocaleString('en-IN')}</span></div>
              </>
          }
          {totals.roundOff !== 0 && <div className="flex justify-between text-gray-400"><span>Round Off</span><span>₹{totals.roundOff.toFixed(2)}</span></div>}
          <div className="flex justify-between font-bold text-lg text-gray-900 border-t border-gray-300 pt-2">
            <span>Total</span><span className="text-indigo-600">₹{totals.grandTotal.toLocaleString('en-IN')}</span>
          </div>
        </div>
      </div>

      {/* ── Notes / Terms ─────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={label}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Any notes for the customer..." rows={3}
            className={inp + ' resize-none'} />
        </div>
        <div>
          <label className={label}>Terms & Conditions</label>
          <textarea value={form.termsConditions} onChange={e => setForm(f => ({ ...f, termsConditions: e.target.value }))}
            placeholder="Payment terms, delivery terms..." rows={3}
            className={inp + ' resize-none'} />
        </div>
      </div>

      {/* ── Actions ──────────────────────────────────────── */}
      <div className="flex gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onClose}
          className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button type="button" disabled={saving}
          onClick={() => submit(true)}
          className="px-5 py-2.5 border border-indigo-200 bg-indigo-50 rounded-xl text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save as Draft'}
        </button>
        <button type="button" disabled={saving}
          onClick={() => submit(false)}
          className="flex-1 px-5 py-2.5 bg-indigo-600 rounded-xl text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving...' : (editing ? 'Update' : `Create ${docType === 'QUOTE' ? 'Quotation' : 'Invoice'}`)}
        </button>
      </div>
    </form>
  );
}
